import { useState } from 'react';
import { View, ActivityIndicator, Pressable, StyleSheet } from 'react-native';
import { DataTable } from 'react-native-paper';
import { Feather } from '@expo/vector-icons';

import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import DeleteUserButton from './DeleteUserButton';
import UpdateUserButton from './UpdateUserButton';
import UserRoles from './UserRoles';
import UserDefaultOrganization from './UserDefaultOrganization';
import ResetUserPassword from './ResetUserPassword';
import UserDetailModal from './UserDetailModal';
import { useTranslation } from '../i18n';

// Always use compact mode - simplified table with modal for editing
// This provides a cleaner, more maintainable UX across all screen sizes

const UserTable = ({
  filteredUsers,
  isEditMode,
  validOrgs,
  editUsers,
  searchText,
  roleSearchValue,
  statusSearchValue,
  OrgFilterValue,
  onSaveRoleUpdate,
  onSaveOrgUpdate,
  onSaveOrgAccessUpdate,
  onSavePasswordReset,
  onEditUser,
  onDeleteUser,
  // Multi-select deletion props
  selectedForDeletion = new Set(),
  onToggleDeleteSelection = null,
  onDeleteSelected = null,
  // Loading state
  usersLoaded = false,
}) => {
  const { t } = useTranslation('admin');

  const { isWeb, screenWidth } = useResponsiveStyles();
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Adjust scale for small web screens
  const scale = isWeb && screenWidth < 900 ? 0.7 : 1;

  // Always use compact mode: simplified table with click-to-edit modal
  const isCompact = true;

  const userKeys = Object.keys(filteredUsers);
  const hasUsers = userKeys.length > 0;
  const filterKey = `${searchText}-${roleSearchValue.join('-')}-${statusSearchValue.join('-')}-${OrgFilterValue.join('-')}`;

  const handleRowPress = (username) => {
    if (isEditMode && isCompact) {
      setSelectedUser(username);
      setModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedUser(null);
  };

  // Show loading spinner while users are being fetched
  if (isEditMode && !hasUsers && !usersLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FFC451" />
      </View>
    );
  }

  // Show empty state when users have loaded but none exist
  if (isEditMode && !hasUsers && usersLoaded) {
    return (
      <View style={styles.emptyState}>
        <Feather name="users" size={48} color={AppStyles.colorSet.textMuted} />
        <CustomText
          font="medium"
          size={18}
          color={AppStyles.colorSet.textSecondary}
          style={{ marginTop: 16, textAlign: 'center' }}
        >
          {t('userTable.empty')}
        </CustomText>
        <CustomText
          font="regular"
          size={14}
          color={AppStyles.colorSet.textMuted}
          style={{ marginTop: 8, textAlign: 'center' }}
        >
          {t('userTable.emptyHint')}
        </CustomText>
      </View>
    );
  }

  return (
    <>
      {isEditMode && hasUsers && (
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <CustomText style={{ color: '#374151', marginLeft: 8 }} size={12} font="medium">
              {t('userTable.tapHint')}
            </CustomText>
            {selectedForDeletion.size > 0 && onDeleteSelected && (
              <Pressable
                onPress={onDeleteSelected}
                style={({ pressed }) => [
                  styles.deleteSelectedButton,
                  pressed && styles.deleteSelectedButtonPressed,
                ]}
              >
                <Feather name="trash-2" size={14} color={AppStyles.colorSet.whiteColor} />
                <CustomText style={{ color: AppStyles.colorSet.whiteColor, marginLeft: 4 }} size={12} font="medium">
                  {t('userTable.deleteSelectedCount', { count: selectedForDeletion.size })}
                </CustomText>
              </Pressable>
            )}
          </View>
          <CustomText style={{ marginRight: 2 }} size={12}>
            {t('userTable.userCount', { count: userKeys.length })}
          </CustomText>
        </View>
      )}

      <DataTable>
        {hasUsers && (
          <DataTable.Header>
            <DataTable.Title style={{ minWidth: isCompact ? 150 : 250 * scale }}>{t('userTable.user')}</DataTable.Title>
            {isEditMode && isCompact && <DataTable.Title style={{ maxWidth: 24, minWidth: 24 }} />}
            <DataTable.Title style={{ minWidth: 50, maxWidth: 50 }}>{t('userTable.status')}</DataTable.Title>
            <DataTable.Title>{t('userTable.roles')}</DataTable.Title>
            {isEditMode && validOrgs?.length > 1 && !isCompact && (
              <DataTable.Title style={{ maxWidth: 120 }}>{t('userTable.organization')}</DataTable.Title>
            )}
            {isEditMode && validOrgs?.length > 1 && !isCompact && (
              <DataTable.Title style={{ maxWidth: 120 }}>{t('userTable.organizationAccess')}</DataTable.Title>
            )}
            {isEditMode && !isCompact && (
              <DataTable.Title style={{ width: 150 * scale }}>{t('userTable.temporaryPassword')}</DataTable.Title>
            )}
            {isEditMode && <DataTable.Title style={{ maxWidth: 100 }}>{t('userTable.actions')}</DataTable.Title>}
          </DataTable.Header>
        )}

        {hasUsers &&
          userKeys.map((username, index) => {
            const user = filteredUsers[username];
            const rowKey = `${index}-${userKeys.length}`;
            const cellKey = `${username}-${userKeys.length}-${filterKey}`;

            const rowContent = (
              <>
                <DataTable.Title
                  key={`${cellKey}-user`}
                  style={{ minWidth: isCompact ? 150 : 250 * scale }}
                >
                  {username}
                </DataTable.Title>
                {isEditMode && isCompact && (
                  <DataTable.Cell style={{ maxWidth: 24, minWidth: 24 }}>
                    <Feather name="chevron-right" size={18} color={AppStyles.colorSet.brandPrimary} />
                  </DataTable.Cell>
                )}

                <DataTable.Title
                  style={{ minWidth: 50, maxWidth: 50 }}
                  key={`${cellKey}-status`}
                >
                  {t(`userStatus.${user.UserStatus}`, { defaultValue: user.UserStatus })}
                </DataTable.Title>

                {isEditMode ? (
                  <DataTable.Cell key={`${cellKey}-roles-edit`}>
                    <UserRoles
                      user={username}
                      roles={user.roles}
                      edit={true}
                      postUpdateAction={onSaveRoleUpdate}
                    />
                  </DataTable.Cell>
                ) : (
                  <DataTable.Cell key={`${cellKey}-roles`}>
                    <UserRoles user={username} roles={user.roles} edit={false} />
                  </DataTable.Cell>
                )}

                {/* Organization - hidden in compact mode */}
                {isEditMode && validOrgs?.length > 1 && !isCompact && (
                  <DataTable.Cell style={{ maxWidth: 120 }} key={`${cellKey}-org`}>
                    <UserDefaultOrganization
                      user={username}
                      organization={user.organization}
                      organizations={validOrgs}
                      postUpdateAction={onSaveOrgUpdate}
                      multiple={false}
                    />
                  </DataTable.Cell>
                )}

                {/* Organization Access - hidden in compact mode */}
                {isEditMode && validOrgs?.length > 1 && !isCompact && (
                  <DataTable.Cell style={{ maxWidth: 120 }} key={`${cellKey}-org-access`}>
                    <UserDefaultOrganization
                      user={username}
                      organization={user.organization_access}
                      organizations={validOrgs}
                      postUpdateAction={onSaveOrgAccessUpdate}
                      multiple={true}
                    />
                  </DataTable.Cell>
                )}

                {/* Temporary Password - hidden in compact mode */}
                {isEditMode && !isCompact && (
                  <DataTable.Cell style={{ width: 150 * scale }}>
                    <ResetUserPassword
                      key={`${cellKey}-reset`}
                      user={username}
                      postUpdateAction={onSavePasswordReset}
                    />
                  </DataTable.Cell>
                )}

                {/* Action buttons - always use icon buttons */}
                {isEditMode && (
                  <DataTable.Cell style={{ maxWidth: 50 }} key={`${cellKey}-edit`}>
                    <UpdateUserButton
                      user={username}
                      organization={user.organization}
                      editUsers={editUsers}
                      postUpdateAction={onEditUser}
                      compact={true}
                    />
                  </DataTable.Cell>
                )}

                {isEditMode && (
                  <DataTable.Cell style={{ maxWidth: 50 }} key={`${cellKey}-delete`} numeric>
                    <DeleteUserButton
                      user={username}
                      organization={user.organization}
                      postDeleteAction={onDeleteUser}
                      compact={true}
                      isSelected={selectedForDeletion.has(username)}
                      onToggleSelect={onToggleDeleteSelection}
                    />
                  </DataTable.Cell>
                )}
              </>
            );

            return (
              <Pressable
                key={rowKey}
                onPress={() => handleRowPress(username)}
                style={({ pressed, hovered }) => [
                  isEditMode && isCompact && styles.rowPressable,
                  isEditMode && isCompact && hovered && styles.rowHovered,
                  isEditMode && isCompact && pressed && styles.rowPressed,
                ]}
              >
                <DataTable.Row style={{ cursor: isEditMode && isCompact ? 'pointer' : 'default' }}>
                  {rowContent}
                </DataTable.Row>
              </Pressable>
            );
          })}
      </DataTable>

      {/* User Detail Modal for compact mode */}
      <UserDetailModal
        visible={modalVisible}
        onClose={handleCloseModal}
        username={selectedUser}
        user={selectedUser ? filteredUsers[selectedUser] : null}
        validOrgs={validOrgs}
        editUsers={editUsers}
        onSaveRoleUpdate={onSaveRoleUpdate}
        onSaveOrgUpdate={onSaveOrgUpdate}
        onSaveOrgAccessUpdate={onSaveOrgAccessUpdate}
        onSavePasswordReset={onSavePasswordReset}
        onEditUser={onEditUser}
        onDeleteUser={onDeleteUser}
      />
    </>
  );
};

const styles = StyleSheet.create({
  rowPressable: {
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: 'transparent',
  },
  rowHovered: {
    backgroundColor: '#F9FAFB',
    borderLeftColor: '#FFC451',
  },
  rowPressed: {
    backgroundColor: '#F3F4F6',
    borderLeftColor: '#D4A000',
  },
  deleteSelectedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.error,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: AppStyles.borderRadii.sm,
    cursor: 'pointer',
  },
  deleteSelectedButtonPressed: {
    backgroundColor: '#DC2626',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
});

export default UserTable;
